import React from 'react';

// Define standard chip colors (can be imported from a shared location later)
const chipColors: { [key: number]: string } = {
  1: '#60a5fa',    // Blue ($1)
  5: '#ef4444',    // Red ($5)
  25: '#22c55e',   // Green ($25)
  100: '#1f2937',  // Black ($100)
  500: '#a855f7',  // Purple ($500)
  1000: '#f59e0b', // Orange/Yellow ($1,000)
  5000: '#6b7280', // Gray ($5,000)
  10000: '#9f1239', // Burgundy ($10,000)
  25000: '#67e8f9', // Light Blue ($25,000)
  50000: '#f472b6', // Pink ($50,000)
  100000: '#eab308' // Gold ($100,000)
};
const defaultChipColor = '#9ca3af'; // Fallback grey

// Simple map for darker inset colors (could be dynamically calculated later)
const chipDarkColors: { [key: number]: string } = {
  1: '#2563eb',    // Darker Blue
  5: '#dc2626',    // Darker Red
  25: '#16a34a',   // Darker Green
  100: '#0f172a',  // Very Dark Grey/Black
  500: '#7e22ce',  // Darker Purple
  1000: '#d97706', // Darker Orange/Yellow
  5000: '#4b5563', // Darker Gray
  10000: '#881337', // Darker Burgundy
  25000: '#0e7490', // Darker Light Blue
  50000: '#db2777', // Darker Pink
  100000: '#ca8a04' // Darker Gold
};
const defaultDarkChipColor = '#4b5563'; // Fallback dark grey

interface PokerChipProps {
  value: number;
  size: number; // Diameter of the chip
}

const PokerChip: React.FC<PokerChipProps> = ({ value, size }) => {
  const mainFill = chipColors[value] || defaultChipColor;
  const labelBgFill = chipDarkColors[value] || defaultDarkChipColor;

  // Adjust font size based on number of digits for better fit
  const valueString = value.toString();
  let fontSize = 48; // Default from SVG
  if (valueString.length > 3) fontSize = 40;
  if (valueString.length > 4) fontSize = 32;
  if (valueString.length > 5) fontSize = 26;

  return (
    <svg viewBox="0 0 220 220" width={size} height={size} xmlns="http://www.w3.org/2000/svg"
         fontFamily="Helvetica, Arial, sans-serif" fontWeight="700" textAnchor="middle">

      {/* Reusable parts */}
      <defs>
        {/* Clipping circle */}
        <clipPath id="rimClip"><circle cx="110" cy="110" r="100"/></clipPath>
        {/* Edge stripe */}
        <rect id="edgeStripe" x="102" y="8" width="16" height="32" rx="2" ry="2" fill="#ffffff"/>
      </defs>

      {/* Main body - Use dynamic color */}
      <circle cx="110" cy="110" r="100" fill={mainFill}/>

      {/* Eight white inlays */}
      <g clipPath="url(#rimClip)">
        <use href="#edgeStripe"/>
        <use href="#edgeStripe" transform="rotate(45 110 110)"/>
        <use href="#edgeStripe" transform="rotate(90 110 110)"/>
        <use href="#edgeStripe" transform="rotate(135 110 110)"/>
        <use href="#edgeStripe" transform="rotate(180 110 110)"/>
        <use href="#edgeStripe" transform="rotate(225 110 110)"/>
        <use href="#edgeStripe" transform="rotate(270 110 110)"/>
        <use href="#edgeStripe" transform="rotate(315 110 110)"/>
      </g>

      {/* Thin white inner ring */}
      <circle cx="110" cy="110" r="85" fill="none" stroke="#ffffff" strokeWidth="6"/>

      {/* Central label background - Use dynamic dark color */}
      <circle cx="110" cy="110" r="68" fill={labelBgFill}/>

      {/* Denomination - Use dynamic value and font size */}
      <text x="110" y="124" fontSize={fontSize} fill="#ffffff">{valueString}</text>

      {/* Four suit icons */}
      <g fill="#ffffff" fontSize="16">
        <text x="110" y="66">♠</text>
        <text x="154" y="110">♥</text>
        <text x="110" y="154">♣</text>
        <text x="66"  y="110">♦</text>
      </g>
    </svg>
  );
};

export default PokerChip; 